// client/src/components/itensVenda/FiltroItensVenda.js - VERSÃO 100% PADRONIZADA
import React, { useState, useMemo } from 'react';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import { useUnidadesParaFormulario } from '../../hooks/useUnidades';
import { useMutateItensVenda } from '../../hooks/useItensVenda';
import { Search, X, Filter, Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';
import useAlert from '../../hooks/useAlert';

const FiltroItensVenda = ({ onFilterSubmit, loading, accountId }) => {
    const { setAlert } = useAlert();
    // Hooks de dados
    const { data: categoriasData, isLoading: loadingCategorias } = useCategoriasParaFormulario(accountId, 'produto');
    const { data: unidadesData, isLoading: loadingUnidades } = useUnidadesParaFormulario(accountId);
    const { sanitizeParams } = useMutateItensVenda();
    
    // Processamento de dados
    const categorias = useMemo(() => {
        if (!categoriasData) return [];
        return categoriasData;
    }, [categoriasData]);

    const unidades = useMemo(() => {
        if (!unidadesData) return [];
        
        if (Array.isArray(unidadesData)) return unidadesData;
        if (unidadesData.data && Array.isArray(unidadesData.data)) return unidadesData.data;
        
        return [];
    }, [unidadesData]);

    // Estado inicial
    const initialFilters = {
        search: '',
        categoria: '',
        'unidadesDetalhes.unidade': '',
        ativo: '',
        visivelParaCliente: '',
        controlaEstoque: '',
        'metadata.tipoItem': '',
        sku: '',
        tags: '',
        temEstoque: '',
        'unidadesDetalhes.disponivel': ''
    };

    const [filters, setFilters] = useState(initialFilters);

    // Handlers
    const onChange = e => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const onSubmit = e => {
        e.preventDefault();
        
        if (filters['unidadesDetalhes.disponivel'] && !filters['unidadesDetalhes.unidade']) {
            setAlert('Para filtrar por disponibilidade, selecione primeiro uma unidade.', 'warning');
            return;
        }
        
        const cleanFilters = sanitizeParams(filters);
        const finalFilters = { ...cleanFilters };
        
        // Tratamento de campos virtuais
        if (finalFilters.temEstoque) {
            if (finalFilters.temEstoque === 'true') {
                finalFilters['unidadesDetalhes.quantidadeEmEstoque'] = { $gt: 0 };
            } else if (finalFilters.temEstoque === 'false') {
                finalFilters['unidadesDetalhes.quantidadeEmEstoque'] = 0;
            }
            delete finalFilters.temEstoque;
        }
        
        if (finalFilters['unidadesDetalhes.disponivel']) {
            finalFilters['unidadesDetalhes.disponivel'] = finalFilters['unidadesDetalhes.disponivel'] === 'true';
            finalFilters['unidadesDetalhes.ativo'] = true;
        }
        
        if (finalFilters['unidadesDetalhes.unidade']) {
            finalFilters['unidadesDetalhes.ativo'] = true;
        }
        
        if (onFilterSubmit) {
            onFilterSubmit(finalFilters);
        }
    };
    
    const onClear = () => {
        setFilters(initialFilters);
        if (onFilterSubmit) onFilterSubmit({});
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== null && value !== undefined);
    const hasUnidadeSelected = filters['unidadesDetalhes.unidade'] !== '';

    return (
        <form onSubmit={onSubmit} className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-end">
                
                {/* 1. Busca Geral */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="search" className="text-xs font-semibold text-gray-600 uppercase">Busca Geral</label>
                    <input 
                        type="text" 
                        name="search" 
                        id="search"
                        placeholder="Nome, descrição..." 
                        value={filters.search} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* 2. SKU */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="sku" className="text-xs font-semibold text-gray-600 uppercase">SKU</label>
                    <input 
                        type="text" 
                        name="sku" 
                        id="sku"
                        placeholder="Código SKU..." 
                        value={filters.sku} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* 3. Categoria */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="categoria" className="text-xs font-semibold text-gray-600 uppercase">Categoria</label>
                    <select
                        name="categoria"
                        id="categoria"
                        value={filters.categoria}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        disabled={loadingCategorias}
                    >
                        <option value="">Todas as categorias</option>
                        {loadingCategorias ? (
                            <option value="" disabled>Carregando...</option>
                        ) : categorias.length > 0 ? (
                            categorias.map(cat => (
                                <option key={cat._id} value={cat._id}>{cat.nome}</option>
                            ))
                        ) : (
                            <option value="" disabled>Nenhuma disponível</option>
                        )}
                    </select>
                </div>

                {/* 4. Unidade Vinculada */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="unidadesDetalhes.unidade" className="text-xs font-semibold text-gray-600 uppercase">Unidade</label>
                    <select
                        name="unidadesDetalhes.unidade"
                        id="unidadesDetalhes.unidade"
                        value={filters['unidadesDetalhes.unidade']}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        disabled={loadingUnidades}
                    >
                        <option value="">Todas as unidades</option>
                        {loadingUnidades ? (
                            <option value="" disabled>Carregando...</option>
                        ) : unidades.length > 0 ? (
                            unidades.map(unidade => (
                                <option key={unidade._id} value={unidade._id}>
                                    {unidade.nome} {unidade.localidade && `- ${unidade.localidade}`}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>Nenhuma disponível</option>
                        )}
                    </select>
                </div>

                {/* 5. Disponibilidade (Condicional) */}
                {hasUnidadeSelected && (
                    <div className="flex flex-col gap-1">
                        <label htmlFor="unidadesDetalhes.disponivel" className="text-xs font-semibold text-gray-600 uppercase">Disponibilidade (Unidade)</label>
                        <select
                            name="unidadesDetalhes.disponivel"
                            id="unidadesDetalhes.disponivel"
                            value={filters['unidadesDetalhes.disponivel']}
                            onChange={onChange}
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Disponíveis</option>
                            <option value="false">Indisponíveis</option>
                        </select>
                    </div>
                )}

                {/* 6. Tipo do Item */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="metadata.tipoItem" className="text-xs font-semibold text-gray-600 uppercase">Tipo</label>
                    <select
                        name="metadata.tipoItem"
                        id="metadata.tipoItem"
                        value={filters['metadata.tipoItem']}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="fisico">Físico</option>
                        <option value="digital">Digital</option>
                    </select>
                </div>

                {/* 7. Status */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="ativo" className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                    <select
                        name="ativo"
                        id="ativo"
                        value={filters.ativo}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="true">Ativos</option>
                        <option value="false">Inativos</option>
                    </select>
                </div>

                {/* 8. Visibilidade */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="visivelParaCliente" className="text-xs font-semibold text-gray-600 uppercase">Visibilidade</label>
                    <select
                        name="visivelParaCliente"
                        id="visivelParaCliente"
                        value={filters.visivelParaCliente}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todas</option>
                        <option value="true">Visíveis</option>
                        <option value="false">Ocultos</option>
                    </select>
                </div>

                {/* 9. Situação Estoque */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="temEstoque" className="text-xs font-semibold text-gray-600 uppercase">Situação Estoque</label>
                    <select
                        name="temEstoque"
                        id="temEstoque"
                        value={filters.temEstoque}
                        onChange={onChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todas</option>
                        <option value="true">Com estoque</option>
                        <option value="false">Sem estoque</option>
                    </select>
                </div>

                {/* 10. Tags */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="tags" className="text-xs font-semibold text-gray-600 uppercase">Tags</label>
                    <input 
                        type="text" 
                        name="tags" 
                        id="tags"
                        placeholder="Tags..." 
                        value={filters.tags} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* Botões de Ação — antes reinventava cor do zero em Tailwind
                    (bg-blue-600) e o texto "Filtrar" so aparecia acima de 640px
                    (hidden sm:inline) */}
                <div className="flex gap-2 h-full items-end md:col-span-2 lg:col-span-1 xl:col-span-1">
                    <button
                        type="submit"
                        disabled={loading || loadingCategorias || loadingUnidades}
                        className="action-button action-button-primary flex-1 justify-center"
                        title="Aplicar Filtros"
                    >
                        {(loading || loadingCategorias || loadingUnidades) ?
                            <Loader2 size={18} className="icon animate-spin" /> :
                            <Search size={18} className="icon" />
                        }
                        Aplicar Filtros
                    </button>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={onClear}
                            disabled={loading}
                            className="action-button action-button-secondary justify-center"
                            title="Limpar Filtros"
                        >
                            <X size={18} className="icon" />
                        </button>
                    )}
                </div>
            </div>

            {/* Resumo de Filtros */}
            {hasActiveFilters && (
                <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                        <Filter size={14} className="text-blue-600" />
                        <span>Filtros ativos:</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                            {Object.values(filters).filter(v => v !== '').length}
                        </span>
                    </div>
                    <button onClick={onClear} className="text-red-600 hover:text-red-800 hover:underline">
                        Limpar todos
                    </button>
                </div>
            )}
        </form>
    );
};

FiltroItensVenda.propTypes = {
    onFilterSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    accountId: PropTypes.string
};

FiltroItensVenda.defaultProps = {
    loading: false
};

export default FiltroItensVenda;